import {Component, Injector} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {CreateListViewModel} from "@app/presentation/pages/create-list/create-list.view-model";
import {Observable} from "rxjs";
import {List} from "@app/core/lists/domain/list";
import {routesNames} from '@app/app-routing.module';
import {PopupDispatcher} from "@app/presentation/components/errors/popup.dispatcher";
import {take} from "rxjs/operators";
import {ExitService} from "@app/presentation/services/exit.service";

import {MatBottomSheetRef} from "@angular/material/bottom-sheet";
import {ToastService} from "@app/presentation/services/toast/toast.service";
import {TranslateService} from "@ngx-translate/core";
import {LocalizedRouter} from "@app/presentation/services/localized-router";
import {ModalService} from "@app/presentation/services/modal/modal.service";

/**
 * Component for creating a new list or renaming an existing one.
 */
@Component({
  selector: 'app-create-list',
  templateUrl: './create-list.component.html',
  styleUrls: ['./create-list.component.scss']
})
export class CreateListComponent {

  listFormGroup = new UntypedFormGroup({
    name: new UntypedFormControl(null, Validators.required),
  });

  listId?: number;

  // Codes of the products that will be included in the new list
  productsCodes: string[] = [];

  routesNames = routesNames;

  isRequestLoading$ = this.createListViewModel.isRequestLoading$;

  isDisplayedInBottomSheet = false;

  constructor(
    private createListViewModel: CreateListViewModel,
    popupDispatcher: PopupDispatcher,
    private injector: Injector,
    private toastService: ToastService,
    private translateService: TranslateService,
    private localizedRouter: LocalizedRouter,
  ) {
    popupDispatcher.setNotifier(this.createListViewModel.notifier);

    const data = ModalService.getModalData(this.injector)
    // If the modal is opened for renaming a list, the listId is passed in the data.
    this.listId = data?.listId;
    // When the modal is opened for creating a new list, can receive the products codes that will be included in the new list.
    this.productsCodes = data?.productsCodes ?? [];

    try {
        //Ensure that this component is displayed in a bottom sheet.
        this.injector.get(MatBottomSheetRef);
        //If the code reaches this point, it means that the component is displayed in a bottom sheet.
        this.isDisplayedInBottomSheet = true;
    } catch {}

  }

  onClickAcceptButton() {
    let response: Observable<List> | null;

    if (this.listId) {
      response = this.renameList()
    } else {
      response = this.createList()
    }

    if (response) {
      response
          .pipe(take(1))
          .subscribe({
            next: list => {
            this.closeWithValue(list.id);
      }});
    }
  }

  closeWithValue(value: number) {
    const isListRenamed = !!this.listId;
    const actionMessageKey = isListRenamed ? 'action_message.lists.successfully_renamed' : 'action_message.lists.successfully_created';
    const onClickAction = isListRenamed ? null : () => this.localizedRouter.navigate(['/', routesNames.listDetail, value]);
    this.toastService.show(this.translateService.instant(actionMessageKey), onClickAction);
    ExitService.exit(this.injector, value);
  }

  private createList(): Observable<List> | null {
    if (!this.listFormGroup.valid) {
      return null
    }
    return this.createListViewModel.createList(this.listFormGroup.controls.name.value, this.productsCodes);
  }

  private renameList(): Observable<List> | null {
    if (this.listFormGroup.valid && this.listId) {
      return this.createListViewModel.renameList(this.listId, this.listFormGroup.controls.name.value)
    }
    return null
  }

}


